import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service';
import jmoment from 'moment-jalaali';
import { excelExportInitialState } from './excelExportInitialState';

export const createExcelTitle = (queryId, page, platform) => {
  const platformSlugMap = {
    instagram: 'instagram',
    instagramComment: 'comment_instagram',
    instagramCommentByPost: 'comment_instagram',
    instagramStories: 'story_instagram',
    twitter: 'twitter',
    virasty: 'virasty',
    telegram: 'telegram',
    news: 'news',
    newsCopy: 'newsCopy',
    newspaper: 'newspaper',
    linkedin: 'linkedin',
    nativePlatforms: 'iranian_platform',
    bale: 'iranian_platform',
    eitaa: 'iranian_platform',
    rubika: 'iranian_platform',
    multiPlatform: 'all',
    digikala: 'digikala',
  };

  const fileName = `${platformSlugMap[platform]}_${jmoment().format(
    'jYYYY-jMM-jDD_HH-mm'
  )}`;

  return fileName;
};

export const fetchExcelExportLimits = createAsyncThunk(
  'excelExportReducer/fetchExcelExportLimits',
  async (payload, thunkAPI) => {
    const { page } = payload;

    const params = page === 'resource' ? { is_resource: 1 } : {};

    try {
      const response = await apiGet('/search/api/v1/excels/limits', params);
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createExcelExport = createAsyncThunk(
  'excelExportReducer/createExcelExport',
  async (payload, thunkAPI) => {
    const excelExportState = thunkAPI.getState().excelExportReducer;
    const { page, bulletinId } = payload;

    // اگر تایتل ست نشده بود یا خالی شده بود بازم حالت دیفالت اسم ساخته و ارسال بشود
    let title = excelExportState?.title;
    if (!title) {
      const { currentQuery } = thunkAPI.getState().pageReducer;

      title = createExcelTitle(
        currentQuery?.queryId,
        page,
        excelExportState.platform
      );
    }

    const apiRoute =
      page === 'content'
        ? '/search/api/v1/excels'
        : '/search/api/v1/excels/resource';

    const params = {
      excelExport: '1',
      excel_title: title,
      has_resource: 0,
      want_count: excelExportState?.count,
      ...excelExportState.selectedParams,
      sort: excelExportState?.isRandomSortEnabled
        ? 'random'
        : excelExportState.selectedParams.sort || undefined,
      format:
        Object.keys(excelExportState?.exportFormats)?.find(
          (x) => excelExportState?.exportFormats[x].selected
        ) || undefined,
      bulletin_id: bulletinId,
    };

    if (
      Object.keys(
        excelExportState.excelFields?.[page]?.[excelExportState?.platform] || []
      ).length > 0
    ) {
      params.want_fields =
        Object.keys(
          excelExportState.excelFields?.[page]?.[excelExportState?.platform]
        ).filter(
          (key) =>
            excelExportState?.excelFields?.[page]?.[excelExportState?.platform][
              key
            ].selected
        ) || undefined;
    }

    try {
      const response = await apiPost(apiRoute, params);
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const excelExportSlice = createSlice({
  name: 'excelExportSlice',
  initialState: excelExportInitialState,
  reducers: {
    resetExcelExport: (state) => {
      state.exportFormats = excelExportInitialState.exportFormats;
      state.export = excelExportInitialState.export;
      state.isRandomSortEnabled = excelExportInitialState.isRandomSortEnabled;
      state.count = excelExportInitialState.count;
      state.step = 'step_1';
      state.excelFields = excelExportInitialState.excelFields;
    },
    changeExportvalue: (state, action) => {
      const { value, key } = action.payload;
      state[key] = value;
    },
    changeExportFormats: (state, action) => {
      Object.keys(state.exportFormats).map((key) => {
        state.exportFormats[key].selected = action?.payload === key;
      });
    },
    setExcelFilters: (state, action) => {
      const { params, exportPage, exportPlatform } = action.payload;
      const nextParams = { ...params };
      delete nextParams.page;
      delete nextParams.count;

      state.selectedParams = nextParams;
      state.page = exportPage;
      state.platform = exportPlatform;
    },
    toggleExportExcelField: (state, action) => {
      const field = action.payload;

      state.excelFields[state.page][state.platform][field].selected =
        !state.excelFields[state.page][state.platform][field].selected;
    },
    toggleAllExportExcelFields: (state, action) => {
      const selected = action.payload;
      Object.keys(state.excelFields[state.page][state.platform]).forEach(
        (key) =>
          (state.excelFields[state.page][state.platform][key].selected =
            selected)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExcelExportLimits.fulfilled, (state, action) => {
        state.limits.data = action.payload;
        state.limits.loading = false;
        state.limits.error = null;
      })
      .addCase(fetchExcelExportLimits.pending, (state) => {
        state.limits.loading = true;
        state.limits.error = null;
      })
      .addCase(fetchExcelExportLimits.rejected, (state, action) => {
        state.limits.loading = false;
        state.limits.error = action.payload;
      })
      .addCase(createExcelExport.fulfilled, (state, action) => {
        state.export.data = action.payload;
        state.export.loading = false;
        state.export.error = null;
      })
      .addCase(createExcelExport.pending, (state) => {
        state.export.loading = true;
        state.export.error = null;
      })
      .addCase(createExcelExport.rejected, (state, action) => {
        state.export.loading = false;
        state.export.error = action.payload;
      });
  },
});

export const {
  changeExportFormats,
  changeExportvalue,
  setExcelFilters,
  toggleExportExcelField,
  toggleAllExportExcelFields,
  resetExcelExport,
} = excelExportSlice.actions;
